import CareSourceByline from './CareSourceByline';
import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';

const Detail = ({ label, children }) => {
  if (!children) return null;
  return (
    <Box>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: alpha(colors.semantic.ink, 0.56) }}>
        {label}
      </Typography>
      <Typography sx={{ mt: 0.1, fontSize: '0.82rem', lineHeight: 1.4, color: alpha(colors.semantic.ink, 0.76), whiteSpace: 'pre-wrap' }}>
        {children}
      </Typography>
    </Box>
  );
};

const CompactReviewRow = ({
  source,
  text,
  destination,
  reason,
  originalText,
  context,
  decision,
  readOnly = false,
  disabled = false,
  quickActionLabel,
  onQuickAdd,
  onReview,
  onSkip,
  onUndo,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box sx={{ borderBottom: `1px solid ${alpha(colors.semantic.ink, 0.09)}`, '&:last-of-type': { borderBottom: 0 } }}>
      <Box sx={{ py: 0.7 }}>
        <Button
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          sx={{
            minWidth: 0,
            flex: 1,
            p: 0,
            justifyContent: 'flex-start',
            textAlign: 'left',
            color: colors.semantic.ink,
            textTransform: 'none',
            borderRadius: '10px',
            '&:hover': { bgcolor: alpha(colors.semantic.sageMist, 0.42) },
            '&:active': { bgcolor: alpha(colors.semantic.sage, 0.12) },
          }}
        >
          <Typography component="span" sx={{ flex: 1, fontSize: '0.9rem', lineHeight: 1.35 }}>
            {text}
          </Typography>
          <ExpandMoreRoundedIcon
            aria-hidden="true"
            sx={{
              ml: 0.4,
              mt: 0.1,
              fontSize: 19,
              color: alpha(colors.semantic.ink, 0.48),
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 180ms ease',
            }}
          />
        </Button>
        <CareSourceByline source={source} />
        {readOnly ? null : decision ? (
          <Stack direction="row" alignItems="center" spacing={0.7} sx={{ mt: 0.4 }}>
            <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: decision === 'add' ? colors.semantic.success : colors.semantic.ink2 }}>
              {decision === 'add' ? 'Added' : 'Skipped'}
            </Typography>
            {decision === 'skip' && onUndo ? (
              <Button size="small" disabled={disabled} onClick={onUndo} sx={{ minWidth: 0, p: 0, textTransform: 'none' }}>
                Undo
              </Button>
            ) : null}
          </Stack>
        ) : (
          <Stack
            role="group"
            aria-label={`${destination} actions`}
            direction="row"
            spacing={{ xs: 0.35, sm: 0.7 }}
            alignItems="center"
            sx={{ mt: 0.45, flexWrap: 'nowrap' }}
          >
            <Button
              size="small"
              variant="text"
              aria-label={quickActionLabel || `Quick add to ${destination}`}
              disabled={disabled}
              onClick={onQuickAdd}
              sx={{
                minWidth: 0,
                minHeight: 32,
                px: 0.7,
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '0.8rem',
                whiteSpace: 'nowrap',
                color: colors.brand.deep,
              }}
            >
              {quickActionLabel || (['Medications', 'Allergies'].includes(destination) ? 'Confirm' : 'Quick add')}
            </Button>
            {onReview ? (
              <Button
                size="small"
                variant="text"
                disabled={disabled}
                onClick={onReview}
                sx={{ minWidth: 0, minHeight: 32, px: 0.7, textTransform: 'none', fontWeight: 800, fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                {['Medications', 'Allergies'].includes(destination) ? 'Review & edit' : 'Review & add'}
              </Button>
            ) : null}
            <Button
              size="small"
              variant="text"
              disabled={disabled}
              onClick={onSkip}
              sx={{
                minWidth: 0,
                minHeight: 32,
                px: 0.7,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8rem',
                whiteSpace: 'nowrap',
                color: alpha(colors.semantic.ink, 0.62),
                '&:hover': { bgcolor: alpha(colors.semantic.ink, 0.045) },
              }}
            >
              {['Medications', 'Allergies'].includes(destination) ? 'Not correct' : 'Skip'}
            </Button>
          </Stack>
        )}
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack spacing={0.7} sx={{ pb: 1, pr: 1, pl: 0.25 }}>
          <Detail label="Why this was suggested">{reason}</Detail>
          <Detail label="Original wording">{originalText}</Detail>
          {!readOnly ? <Detail label="Where it will be saved">{destination}</Detail> : null}
          <Detail label="Supporting context">{context}</Detail>
        </Stack>
      </Collapse>
    </Box>
  );
};

export default CompactReviewRow;
